import React, { useEffect, useRef, useState } from "react";
import { Box, Typography, Avatar, Button, TextField, Card, Alert } from "@mui/material";
import Typed from "typed.js";
import imageone from "../../images/kineticAdminPanelBg.png";
import logo from "../../images/kineticlogo1.png";
import { useNavigate } from "react-router-dom";
import { jwtDecode } from "jwt-decode";

const AnimatedText: React.FC = () => {
  const typedRef = useRef<HTMLSpanElement | null>(null);
  const typedInstance = useRef<Typed | null>(null);

  useEffect(() => {
    if (typedRef.current) {
      typedInstance.current = new Typed(typedRef.current, {
        strings: ["Trucks", "Automobiles", "Diagnostics"],
        typeSpeed: 50,
        backSpeed: 30,
        loop: true,
      });
    }
    return () => typedInstance.current?.destroy();
  }, []);

  return (
    <div style={{ textAlign: "center", marginTop: "10px", fontWeight: "bold", fontSize: "2rem", color: "red" }}>
      <span ref={typedRef} />
    </div>
  );
};

const LoginPage: React.FC = () => {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/user/login`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ username, password }),
      });

      if (!response.ok) {
        throw new Error("Invalid username or password.");
      }

      const jsonData = await response.json();
      const decodedToken: any = jwtDecode(jsonData.token);

      // ✅ Role-based restriction check
      const roleName = decodedToken.role?.name;

      // Only allow users with 'kinetic_admin' or 'super_admin' roles
      if (roleName !== "kinetic_admin" && roleName !== "super_admin") {
        throw new Error("Access denied. Unauthorized user .");
      }

      // Save token and user info to localStorage
      localStorage.setItem("decodedToken", JSON.stringify(decodedToken));
      localStorage.setItem("token", jsonData.token);
      localStorage.setItem("user", JSON.stringify(jsonData));

      // Navigate to the home page
      navigate("/home");

    } catch (error: any) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        justifyContent: "center",
        alignItems: "center",
        height: "100vh",
        display: "flex",
        backgroundImage: `url(${imageone})`,
        backgroundPosition: "left center",
        backgroundSize: "100%",
        backgroundRepeat: "no-repeat",
        backgroundColor: "rgba(0, 0, 0, 0.72)",
      }}
    >
      <Card
        sx={{
          width: "400px",
          padding: 4,
          boxShadow: 6,
          borderRadius: 8,
          backgroundColor: "#ffffff",
          backdropFilter: "blur(5px)",
          border: "1px solid rgba(255, 255, 255, 0.3)",
        }}
      >
        <Box sx={{ mb: 3, textAlign: "center" }}>
          <Avatar sx={{ bgcolor: "#61A143", width: 56, height: 56, margin: "0 auto" }}>
            <img src={logo} alt="Logo" style={{ width: "40px", height: "40px", borderRadius: "50%" }} />
          </Avatar>
          <Typography variant="h5" sx={{ mt: 2, fontWeight: "bold" }}>
            KINETIC
          </Typography>
          <AnimatedText />
        </Box>

        {error && <Alert severity="error">{error}</Alert>}

        <Box component="form" sx={{ display: "flex", flexDirection: "column", gap: 2 }} onSubmit={handleLogin}>
          <TextField
            label="Username"
            variant="outlined"
            fullWidth
            required
            value={username}
            onChange={(e) => setUsername(e.target.value)}
          />
          <TextField
            label="Password"
            type="password"
            variant="outlined"
            fullWidth
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
          <Typography variant="body2" sx={{ textAlign: "right", mb: 1 }}>
            <span
              onClick={() => navigate("/forgotpassword")}
              style={{ color: "red", textDecoration: "none", cursor: "pointer" }}
            >
              Forgot Password?
            </span>
          </Typography>
          <Button
            type="submit"
            variant="contained"
            fullWidth
            disabled={loading}
            sx={{
              mt: 2,
              color: "white",
              background: "red",
              "&:hover": { background: "#590c0c" },
            }}
          >
            {loading ? "Logging in..." : "Login"}
          </Button>
        </Box>
      </Card>
    </Box>
  );
};

export default LoginPage;
